import { FC, Fragment, useState } from 'react';
import { Outbound } from 'ts/flight/interfaces/FlightDetailsResponse';
import { getElapsedStr, getStopsText } from 'utils/flight/flight';
import { getLogoUrl } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCartFlatbedSuitcase,
  faPersonWalkingLuggage,
  faSuitcaseRolling,
} from '@fortawesome/free-solid-svg-icons';
import TicketBaggagePopup from '../Result/TicketBaggagePopup';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { dateFormat } from 'utils/shared/utils';
import { blobUrl } from 'constants/generic';

interface BoundsListProps {
  bounds: Outbound[];
}

const BoundsList: FC<BoundsListProps> = ({ bounds }) => {
  const [t] = useTranslation();
  
  const [showDetailsIndex, setShowDetailsIndex] = useState(-1);

  const isRedHandbag = (bound: Outbound) =>
    !bound.listOfFlyBaggagePrice ||
    !bound.listOfFlyBaggagePrice?.[0] ||
    bound.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag === 0;
  const isRedTrolley = (bound: Outbound) =>
    !bound.listOfFlyBaggagePrice ||
    !bound.listOfFlyBaggagePrice?.[0] ||
    bound.listOfFlyBaggagePrice?.[0]?.maxWeightTrolley === 0;
  const isGreenTotal = (bound: Outbound) =>
    bound.listOfFlyBaggagePrice &&
    (bound.listOfFlyBaggagePrice?.[0]?.maxTotalWeight > 0 ||
      bound.listOfFlyBaggagePrice?.[0]?.pieces > 0);

  return (
    <Fragment>
      {bounds.map((bound, index) => (
        <div className="summary" key={index}>
          <div className="summary-road">
            <span className="summary-road-img">
              <img src={getLogoUrl(blobUrl, bound.airline.code)} alt={bound.airline.desc} />
              <span>{bound.airline.desc}</span>
            </span>
            <div className="w100p text-xsm flex-v-center">
              {!!bound.elapsedDTime && (
                <span className="mr-5">
                  {t('filter.duration')}: {getElapsedStr(t, bound.elapsedDTime)}
                </span>
              )}
              <span className="text-light">({getStopsText(t, bound)})</span>
            </div>
          </div>
          {bound.gdsFlyAirs.map((gds, gdsIndex) => (
            <div className="mb-10" key={gdsIndex}>
              <div className="summary-road">
                <div className="summary-road-item">
                  <div>
                    <span className="flex-v-center">
                      <ReactCountryFlag
                        countryCode={gds.departureAirport.countryCode}
                        svg
                        className="mr-5"
                      />
                      {gds.departureAirport.cityDesc} ({gds.departureAirport.code})
                    </span>
                    <span className="text-light text-xsm">{gds.departureAirport.desc}</span>
                  </div>
                  <div>
                    <span>{dateFormat(gds.departureDateTime, 'EEE - dd MMMM, yyyy')} </span>
                    <span className="text-bold">{dateFormat(gds.departureDateTime, 'HH:mm')}</span>
                  </div>
                </div>
                <div className="summary-road-item">
                  <div>
                    <span className="flex-v-center">
                      <ReactCountryFlag
                        countryCode={gds.arrivalAirport.countryCode}
                        svg
                        className="mr-5"
                      />
                      {gds.arrivalAirport.cityDesc} ({gds.arrivalAirport.code})
                    </span>
                    <span className="text-light text-xsm">{gds.arrivalAirport.desc}</span>
                  </div>
                  <div>
                    <span>{dateFormat(gds.arrivalDateTime, 'EEE - dd MMMM, yyyy')} </span>
                    <span className="text-bold">{dateFormat(gds.arrivalDateTime, 'HH:mm')}</span>
                  </div>
                </div>
              </div>
              {gdsIndex !== bound.gdsFlyAirs.length - 1 && (
                <div className="flex-center mt-10 text-xsm text-warning">
                  <span>
                    {t('filter.stop_up')} {t('at')}{' '}
                  </span>
                  <span className="ph-5">
                    {bound.gdsFlyAirs[gdsIndex].arrivalAirport.cityDesc}{' '}
                  </span>
                  <span>{getElapsedStr(t, bound.gdsFlyAirs[gdsIndex + 1].connDTime)}</span>
                </div>
              )}
            </div>
          ))}
          <div
            className="text-lg mt-5 position-relative"
            onMouseEnter={() => setShowDetailsIndex(index)}
            onMouseLeave={() => setShowDetailsIndex(-1)}
          >
            {showDetailsIndex === index && <TicketBaggagePopup outbond={bound} />}
            {bound.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag !== -1 && (
              <span className="mr-10">
                <FontAwesomeIcon
                  icon={faSuitcaseRolling as any}
                  className={isRedHandbag(bound) ? 'color-danger' : 'text-success'}
                />
              </span>
            )}

            {bound.listOfFlyBaggagePrice?.[0]?.maxWeightTrolley !== -1 && (
              <span className="mr-10">
                <FontAwesomeIcon
                  icon={faPersonWalkingLuggage as any}
                  className={isRedTrolley(bound) ? 'color-danger' : 'text-success'}
                />
              </span>
            )}
            {bound.listOfFlyBaggagePrice?.[0]?.maxTotalWeight !== -1 && (
              <span className="mr-10">
                <FontAwesomeIcon
                  icon={faCartFlatbedSuitcase as any}
                  className={isGreenTotal(bound) ? 'text-success' : 'color-danger'}
                />
              </span>
            )}
          </div>
        </div>
      ))}
    </Fragment>
  );
};

export default withErrorBoundary(BoundsList, 'BoundsList');
